import { ArtButton, ArtTextField, ArtSelect, useApiService } from "@art/shared-ui";
import { faArrowLeft, faDiagramProject, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    Chip,
    CircularProgress,
    Divider,
    Grid2 as Grid,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import { Visibility, VisibilityOff, MailOutline, CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";
import { usePageWrapper } from "../../../hooks";
import { RootState } from "../../../store";

interface FormState {
    smtp_host: string;
    smtp_port: string;
    smtp_encryption: "starttls" | "ssl";
    smtp_username: string;
    smtp_password: string;
    from_name: string;
}

const EMPTY_FORM: FormState = {
    smtp_host: "",
    smtp_port: "587",
    smtp_encryption: "starttls",
    smtp_username: "",
    smtp_password: "",
    from_name: "",
};


const PROVIDER_MAP: Record<string, { name: string; host: string; port: string; encryption: "starttls" | "ssl"; hint: string }> = {
    "gmail.com": { name: "Gmail", host: "smtp.gmail.com", port: "587", encryption: "starttls", hint: "Detected Gmail — use an App Password (Google Account → Security → App passwords), not your normal password." },
    "googlemail.com": { name: "Gmail", host: "smtp.gmail.com", port: "587", encryption: "starttls", hint: "Detected Gmail — use an App Password (Google Account → Security → App passwords), not your normal password." },
    "outlook.com": { name: "Outlook", host: "smtp.office365.com", port: "587", encryption: "starttls", hint: "Detected Outlook — if sign-in fails, your account may need an app password or SMTP AUTH enabled." },
    "hotmail.com": { name: "Outlook", host: "smtp.office365.com", port: "587", encryption: "starttls", hint: "Detected Outlook/Hotmail — if sign-in fails, your account may need an app password or SMTP AUTH enabled." },
    "live.com": { name: "Outlook", host: "smtp.office365.com", port: "587", encryption: "starttls", hint: "Detected Outlook — if sign-in fails, your account may need an app password or SMTP AUTH enabled." },
    "yahoo.com": { name: "Yahoo Mail", host: "smtp.mail.yahoo.com", port: "587", encryption: "starttls", hint: "Detected Yahoo Mail — generate an App Password under Account Security." },
    "zoho.com": { name: "Zoho Mail", host: "smtp.zoho.com", port: "587", encryption: "starttls", hint: "Detected Zoho Mail — if 2FA is on, use an App Password (Zoho Mail → Settings → Security)." },
    "zoho.in": { name: "Zoho Mail", host: "smtp.zoho.in", port: "587", encryption: "starttls", hint: "Detected Zoho Mail (India DC) — if 2FA is on, use an App Password." },
    "icloud.com": { name: "iCloud Mail", host: "smtp.mail.me.com", port: "587", encryption: "starttls", hint: "Detected iCloud Mail — generate an app-specific password at appleid.apple.com." },
};

const PASSWORD_MASK = "••••••••";


function EmailConfigEditorPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const apiService = useApiService();
    const isEdit = Boolean(id);

    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [provider, setProvider] = useState<"smtp" | "google">("smtp");
    const [connecting, setConnecting] = useState(false);

    const appState = useSelector((state: RootState) => state.appstate);
    const projectKey: string = appState?.project_key || "";
    const projectName: string = appState?.project_name || projectKey;
    const environment: string = appState?.environment || "";
    const scopeReady = !!projectKey && !!environment;

    const [configScope, setConfigScope] = useState<{ projectName: string; environment: string } | null>(null);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await apiService.get(`/notification/email-configuration/${id}`);
                const config = response?.data?.data;
                if (config) {
                    setConfigScope({
                        projectName: config.project_name || config.project_key || "",
                        environment: config.environment_name || "",
                    });
                    setForm({
                        smtp_host: config.smtp_host ?? "",
                        smtp_port: String(config.smtp_port ?? 587),
                        smtp_encryption: config.smtp_encryption ?? "starttls",
                        smtp_username: config.smtp_username ?? "",
                        smtp_password: PASSWORD_MASK, // masked; only sent if changed
                        from_name: config.from_name ?? "",
                    });
                }
            } catch (error: any) {
                apiService.showSnack(
                    error?.response?.data?.message || "Failed to load email configuration",
                    "error"
                );
                navigate("/application-settings/messaging/email");
            }
        };
        if (id) fetchConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const set = (key: keyof FormState, value: any) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const [providerHint, setProviderHint] = useState<string | null>(null);

    const handleUsernameChange = (value: string) => {
        set("smtp_username", value);
        const domain = value.split("@")[1]?.toLowerCase().trim();
        const provider = domain ? PROVIDER_MAP[domain] : undefined;
        if (provider) {
            setForm((f) => ({
                ...f,
                smtp_username: value,
                smtp_host: f.smtp_host && f.smtp_host !== provider.host && wasAutoFilled(f.smtp_host) === false ? f.smtp_host : provider.host,
                smtp_port: provider.port,
                smtp_encryption: provider.encryption,
            }));
            setErrors((e) => ({ ...e, smtp_host: undefined, smtp_port: undefined }));
            setProviderHint(provider.hint);
        } else {
            setProviderHint(null);
        }
    };

    const wasAutoFilled = (host: string) =>
        Object.values(PROVIDER_MAP).some((p) => p.host === host);

    const validate = (): boolean => {
        const e: Partial<Record<keyof FormState, string>> = {};
        if (!form.smtp_host.trim()) e.smtp_host = "SMTP host is required";
        if (!/^\d+$/.test(form.smtp_port)) e.smtp_port = "Port must be a number";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.smtp_username)) e.smtp_username = "Enter a valid email address";
        if (!form.smtp_password.trim()) e.smtp_password = "Password is required";
        if (!form.from_name.trim()) e.from_name = "From name is required";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const exchangeGoogleCode = async (payload: { code?: string | null; state?: string | null; error?: string | null }) => {
        if (payload.error) {
            apiService.showSnack(
                payload.error === "access_denied" ? "Google connection was cancelled" : `Google connection failed: ${payload.error}`,
                "error"
            );
            setConnecting(false);
            return;
        }
        if (!payload.code || !payload.state) {
            apiService.showSnack("Invalid response from Google — try again", "error");
            setConnecting(false);
            return;
        }
        try {
            const response = await apiService.post("/notification/email-configuration/oauth/google/exchange", {
                code: payload.code,
                state: payload.state,
            });
            const connectedEmail = response?.data?.data?.connected_email;
            apiService.showSnack(`Google account connected${connectedEmail ? `: ${connectedEmail}` : ""}`, "success");
            navigate("/application-settings/messaging/email");
        } catch (error: any) {
            apiService.showSnack(
                error?.response?.data?.message || "Failed to connect the Google account",
                "error"
            );
            setConnecting(false);
        }
    };


    const handleConnectGoogle = async () => {
        if (!scopeReady) return;

        setConnecting(true);
        try {
            const response = await apiService.get(
                "/notification/email-configuration/oauth/google/authorize"
            );
            const authUrl = response?.data?.data?.auth_url;
            if (!authUrl) {
                apiService.showSnack("Failed to start the Google connection", "error");
                setConnecting(false);
                return;
            }

            const width = 520;
            const height = 680;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;
            const popup = window.open(
                authUrl,
                "art-google-oauth",
                `popup=yes,width=${width},height=${height},left=${left},top=${top}`
            );

            if (!popup) {
                // Popup blocked → classic full-page redirect (params survive a
                // login roundtrip via the sessionStorage stash in App.tsx).
                window.location.href = authUrl;
                return;
            }

            let done = false;
            const channel = new BroadcastChannel("email_oauth_callback");
            const cleanup = () => {
                channel.close();
                window.removeEventListener("message", onWindowMessage);
                clearInterval(popupWatcher);
            };
            const handlePayload = (data: any) => {
                if (done || !data || data.type !== "email_oauth_callback") return;
                done = true;
                cleanup();
                exchangeGoogleCode(data);
            };
            channel.onmessage = (event) => handlePayload(event.data);
            const onWindowMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;
                handlePayload(event.data);
            };
            window.addEventListener("message", onWindowMessage);
            const popupWatcher = setInterval(() => {
                if (popup.closed && !done) {
                    // closed without completing (small grace for in-flight message)
                    setTimeout(() => {
                        if (!done) {
                            done = true;
                            cleanup();
                            setConnecting(false);
                        }
                    }, 500);
                }
            }, 800);
        } catch (error: any) {
            apiService.showSnack(
                error?.response?.data?.message || "Failed to start the Google connection",
                "error"
            );
            setConnecting(false);
        }
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        const payload = {
            smtp_host: form.smtp_host.trim(),
            smtp_port: parseInt(form.smtp_port, 10),
            smtp_encryption: form.smtp_encryption,
            smtp_username: form.smtp_username.trim(),
            smtp_password: form.smtp_password === PASSWORD_MASK ? "" : form.smtp_password,
            from_name: form.from_name.trim(),
            from_email: form.smtp_username.trim(),
        };
        try {
            if (isEdit) {
                await apiService.put(`/notification/email-configuration/${id}`, payload);
                apiService.showSnack("Email configuration updated — send a test email to re-verify it", "success");
            } else {
                await apiService.post("/notification/email-configuration", { ...payload, smtp_password: form.smtp_password });
                apiService.showSnack("Email configuration created — send a test email from the list to verify it", "success");
            }
            navigate("/application-settings/messaging/email");
        } catch (error: any) {
            apiService.showSnack(
                error?.response?.data?.message || `Failed to ${isEdit ? "update" : "create"} email configuration`,
                "error"
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 920 }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <IconButton size="small" onClick={() => navigate("/application-settings/messaging/email")} data-qa="btn-back">
                    <FontAwesomeIcon icon={faArrowLeft} />
                </IconButton>
                <Typography variant="h6" fontWeight={600}>
                    {isEdit ? "Edit Email Configuration" : "New Email Configuration"}
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 5.5 }}>
                Connect the selected project &amp; environment to your own email account. Platform
                emails for this scope will be sent from your address.
            </Typography>

            <SectionTitle
                title="Project & Environment"
                subtitle={
                    isEdit
                        ? "The scope this sender was created for. It cannot be changed — create a new configuration for a different scope."
                        : "This sender applies to the currently selected project and environment."
                }
            />
            <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap", alignItems: "center" }}>
                <Chip
                    icon={<FontAwesomeIcon icon={faDiagramProject} style={{ fontSize: 13 }} />}
                    label={(isEdit ? configScope?.projectName : projectName) || "No project selected"}
                    variant="outlined"
                    data-qa="email-config-project-badge"
                    onClick={isEdit ? undefined : () => navigate("/application-settings/general")}
                />
                <Chip
                    icon={<FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: 13 }} />}
                    label={(isEdit ? configScope?.environment : environment) || "No environment selected"}
                    variant="outlined"
                    data-qa="email-config-env-badge"
                    onClick={isEdit ? undefined : () => navigate("/application-settings/general")}
                />
            </Box>

            {!isEdit && !scopeReady && (
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    Select a project and environment in{" "}
                    <Link component="button" underline="hover" onClick={() => navigate("/application-settings/general")} sx={{ verticalAlign: "baseline" }}>
                        App Settings
                    </Link>{" "}
                    first — email senders are configured per project and environment.
                </Alert>
            )}

            {/* Connection method */}
            {!isEdit && scopeReady && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <SectionTitle
                        title="How do you want to connect?"
                        subtitle="Pick the option that matches your email account."
                    />
                    <Grid container spacing={2}>
                        <ProviderCard
                            selected={provider === "google"}
                            onClick={() => setProvider("google")}
                            dataQa="provider-google"
                            icon={<GoogleGIcon size={24} />}
                            title="Gmail / Google Workspace"
                            caption="Sign in with Google — two clicks, no passwords shared with us."
                        />
                        <ProviderCard
                            selected={provider === "smtp"}
                            onClick={() => setProvider("smtp")}
                            dataQa="provider-smtp"
                            icon={<MailOutline sx={{ fontSize: 24, color: "text.secondary" }} />}
                            title="Other Email Provider"
                            caption="Zoho, Yahoo, company mail servers — via SMTP with an app password."
                        />
                    </Grid>
                </>
            )}

            {!isEdit && !scopeReady ? null : provider === "google" && !isEdit ? (
                <>
                    <Divider sx={{ my: 3 }} />
                    <SectionTitle
                        title="Connect Google Account"
                        subtitle="You'll be redirected to Google to sign in and allow sending emails. Your name and email are taken from your Google account — no password is shared with us."
                    />
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
                        <Button
                            data-qa="btn-connect-google"
                            onClick={() => {
                                if (!connecting) handleConnectGoogle();
                            }}
                            startIcon={connecting ? <CircularProgress size={18} /> : <GoogleGIcon size={18} />}
                            disabled={connecting}
                            sx={{
                                textTransform: "none",
                                bgcolor: "#ffffff",
                                color: "#3c4043",
                                border: "1px solid #dadce0",
                                borderRadius: "4px",
                                px: 2.5,
                                py: 1,
                                fontWeight: 500,
                                fontFamily: "'Roboto', Arial, sans-serif",
                                "&:hover": { bgcolor: "#f8f9fa", borderColor: "#d2e3fc" },
                            }}
                        >
                            {connecting ? "Waiting for Google..." : "Continue with Google"}
                        </Button>
                        <ArtButton
                            variant="outlined"
                            color="error"
                            dataQa="btn-cancel-google"
                            label="Cancel"
                            onClick={() => navigate("/application-settings/messaging/email")}
                        />
                    </Box>
                    <Alert severity="info" variant="outlined">
                        After you allow access, the sender is created automatically — already
                        verified, no test needed.
                    </Alert>
                </>
            ) : (
                <>
            <Divider sx={{ my: 3 }} />

            {/* SMTP settings */}
            <SectionTitle
                title="SMTP Settings"
                subtitle="Credentials of your email account. For Gmail use an App Password (Google Account → Security → App passwords)."
            />
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ArtTextField
                        label="SMTP Host" placeholder="smtp.example.com"
                        value={form.smtp_host}
                        error={errors.smtp_host}
                        onChange={(e: any) => set("smtp_host", e.target.value)}
                        dataQa="input-smtp-host"
                        marginBottom={0}
                    />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <ArtTextField
                        label="Port"
                        value={form.smtp_port}
                        error={errors.smtp_port}
                        onChange={(e: any) => set("smtp_port", e.target.value)}
                        dataQa="input-smtp-port"
                        marginBottom={0}
                    />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <ArtSelect
                        id="encryption"
                        label="Encryption"
                        value={form.smtp_encryption}
                        options={[
                            { label: "STARTTLS", value: "starttls" },
                            { label: "SSL/TLS", value: "ssl" },
                        ]}
                        onChange={(e: any) => set("smtp_encryption", e.target.value)}
                        dataQa="select-encryption"
                        marginBottom={0}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ArtTextField
                        label="Email Address" placeholder="you@yourdomain.com"
                        value={form.smtp_username}
                        error={errors.smtp_username}
                        onChange={(e: any) => handleUsernameChange(e.target.value)}
                        dataQa="input-smtp-username"
                        marginBottom={0}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        fullWidth size="small" label="Password / App Password"
                        type={showPassword ? "text" : "password"}
                        value={form.smtp_password}
                        error={!!errors.smtp_password}
                        helperText={errors.smtp_password ?? (isEdit ? "Leave unchanged to keep the current password" : undefined)}
                        onChange={(e) => set("smtp_password", e.target.value)}
                        data-qa="input-smtp-password"
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setShowPassword((s) => !s)}>
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>
            </Grid>

            {providerHint && (
                <Box sx={{ mt: 2 }}>
                    <Alert severity="success" variant="outlined" onClose={() => setProviderHint(null)}>
                        {providerHint} Server settings were filled in automatically.
                    </Alert>
                </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Sender identity */}
            <SectionTitle title="Sender Identity" subtitle="How the sender appears to recipients. Emails are sent from your username address." />
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ArtTextField
                        label="From Name" placeholder="Acme Support"
                        value={form.from_name}
                        error={errors.from_name}
                        onChange={(e: any) => set("from_name", e.target.value)}
                        dataQa="input-from-name"
                        marginBottom={0}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ArtTextField
                        label="From Email"
                        value={form.smtp_username}
                        disabled
                        dataQa="input-from-email"
                        marginBottom={0}
                    />
                </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
                <Alert severity="info" variant="outlined">
                    After saving, use <b>Send Test Email</b> on the configuration in the list to
                    verify the connection — a test email will be sent to your address.
                </Alert>
            </Box>

            {/* Actions */}
            <Box sx={{ display: "flex", gap: 2, mt: 4, mb: 2, alignItems: "center" }}>
                <ArtButton
                    variant="contained"
                    dataQa="btn-save-email-config"
                    label={saving ? "Saving..." : isEdit ? "Update Configuration" : "Save Configuration"}
                    onClick={() => {
                        if (!saving) handleSave();
                    }}
                />
                {saving && <CircularProgress size={20} />}
                <ArtButton
                    variant="outlined"
                    color="error"
                    dataQa="btn-cancel"
                    label="Cancel"
                    onClick={() => navigate("/application-settings/messaging/email")}
                />
            </Box>
                </>
            )}
        </Box>
    );
}


function ProviderCard({
    selected,
    onClick,
    dataQa,
    icon,
    caption,
}: {
    selected: boolean;
    onClick: () => void;
    dataQa: string;
    icon: React.ReactNode;
    title: string;
    caption: string;
}) {
    return (
        <Grid size={{ xs: 12, sm: 6 }}>
            <Card
                variant="outlined"
                sx={{
                    height: "100%",
                    borderColor: selected ? "primary.main" : "divider",
                    borderRadius: 2,
                    bgcolor: selected ? "rgba(25, 118, 210, 0.04)" : "background.paper",
                    transition: "border-color 0.15s, background-color 0.15s",
                    "&:hover": { borderColor: "primary.main" },
                }}
            >
                <CardActionArea
                    component="div"
                    onClick={onClick}
                    data-qa={dataQa}
                    sx={{ p: 2, height: "100%", cursor: "pointer" }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "#fff",
                                flexShrink: 0,
                            }}
                        >
                            {icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                       
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                                {caption}
                            </Typography>
                        </Box>
                        {selected ? (
                            <CheckCircle color="primary" sx={{ fontSize: 22, flexShrink: 0 }} />
                        ) : (
                            <RadioButtonUnchecked sx={{ fontSize: 22, flexShrink: 0, color: "divider" }} />
                        )}
                    </Box>
                </CardActionArea>
            </Card>
        </Grid>
    );
}

function GoogleGIcon({ size = 24 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
    );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Box>
    );
}

export default usePageWrapper(EmailConfigEditorPage, {
    page_title: "Email Configuration",
});
